import { useEditor, EditorContent } from "@tiptap/react";
import { Node } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import { Underline } from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import TextAlign from "@tiptap/extension-text-align";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { filterTextVariables, type TextVariable } from "@/components/editor/textVariables";

const EDITOR_STYLE_ID = "hotspot-text-editor-styles";
const EDITOR_CSS = `
  .hotspot-text-editor .ProseMirror { outline: none; }
  .hotspot-text-editor .ProseMirror p { margin: 0; min-height: 1.4em; }
  .hotspot-text-editor .ProseMirror ul { list-style-type: disc; padding-left: 1rem; margin: 0; }
  .hotspot-text-editor .ProseMirror ol { list-style-type: decimal; padding-left: 1rem; margin: 0; }
  .hotspot-text-editor .ProseMirror li { margin: 0; }
  .hotspot-text-editor .ProseMirror ul[data-type="taskList"] { list-style: none !important; padding-left: 0 !important; }
  .hotspot-text-editor .ProseMirror li[data-type="taskItem"] { display: flex !important; align-items: flex-start !important; gap: 4px; list-style: none !important; }
  .hotspot-text-editor .ProseMirror li[data-type="taskItem"] > label { flex: 0 0 auto; margin-top: 2px; }
  .hotspot-text-editor .ProseMirror li[data-type="taskItem"] > div,
  .hotspot-text-editor .ProseMirror li[data-type="taskItem"] > span { flex: 1; min-width: 0; }
`;

const IFrame = Node.create({
  name: "iframe",
  group: "block",
  atom: true,
  selectable: true,
  draggable: false,
  addAttributes() {
    return {
      src: { default: null },
      width: { default: "100%" },
      height: { default: "200" },
      frameborder: { default: "0" },
      loading: { default: "lazy" },
      referrerpolicy: { default: "no-referrer" },
      allowfullscreen: { default: "true" },
      title: { default: null },
      allow: { default: null },
    };
  },
  parseHTML() {
    return [{ tag: "iframe" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["iframe", HTMLAttributes];
  },
});
import {
  Bold,
  Code,
  Italic,
  Underline as UnderlineIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  ListChecks,
  ChevronDown,
} from "lucide-react";

interface HotspotTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  onAutoFocusApplied?: () => void;
}

export const HotspotTextEditor = ({
  value,
  onChange,
  placeholder = "Enter text...",
  className,
  autoFocus = false,
  onAutoFocusApplied,
}: HotspotTextEditorProps) => {
  // Inject editor CSS into <head> once so attribute selectors are guaranteed to apply
  useLayoutEffect(() => {
    if (!document.getElementById(EDITOR_STYLE_ID)) {
      const style = document.createElement("style");
      style.id = EDITOR_STYLE_ID;
      style.textContent = EDITOR_CSS;
      document.head.appendChild(style);
    }
  }, []);

  const [listDropdownOpen, setListDropdownOpen] = useState(false);
  const listDropdownRef = useRef<HTMLDivElement>(null);
  const textVariableDropdownRef = useRef<HTMLDivElement>(null);
  const [textVariableMatch, setTextVariableMatch] = useState<{
    from: number;
    to: number;
    query: string;
  } | null>(null);
  const textVariableMatchRef = useRef<typeof textVariableMatch>(null);
  const [activeVariableIndex, setActiveVariableIndex] = useState(0);
  const activeVariableIndexRef = useRef(0);
  const textVariableSuggestionsRef = useRef<TextVariable[]>([]);

  const textVariableSuggestions = useMemo<TextVariable[]>(
    () => (textVariableMatch ? filterTextVariables(textVariableMatch.query) : []),
    [textVariableMatch],
  );

  useEffect(() => {
    textVariableMatchRef.current = textVariableMatch;
  }, [textVariableMatch]);

  useEffect(() => {
    textVariableSuggestionsRef.current = textVariableSuggestions;
    if (textVariableSuggestions.length > 0) {
      setActiveVariableIndex(0);
    }
  }, [textVariableSuggestions]);

  useEffect(() => {
    activeVariableIndexRef.current = activeVariableIndex;
  }, [activeVariableIndex]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false, codeBlock: false, code: false, blockquote: false, horizontalRule: false }),
      Underline,
      TextStyle,
      Color,
      TextAlign.configure({ types: ["paragraph"] }),
      TaskList,
      TaskItem.configure({ nested: false }),
      IFrame,
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      console.log("[Hotspot] editor update", {
        textLength: html.length,
        preview: html.slice(0, 80),
      });
      onChange(html === "<p></p>" ? "" : html);
    },
    editorProps: {
      attributes: {
        class: "outline-none min-h-[72px] text-xs text-white p-2",
      },
      handlePaste: (view, event) => {
        const clipboard = event.clipboardData;
        if (!clipboard) return false;
        const html = clipboard.getData("text/html");
        const text = clipboard.getData("text/plain");

        const extractIframeSrc = (markup: string) => {
          const match = markup.match(/<iframe[^>]+src=["']([^"']+)["'][^>]*>/i);
          return match?.[1] ?? null;
        };

        const src = extractIframeSrc(html || text);
        if (!src) return false;
        const safeSrc = src.trim();
        if (!/^https?:\/\//i.test(safeSrc)) return false;

        editor
          ?.chain()
          .focus()
          .insertContent({
            type: "iframe",
            attrs: {
              src: safeSrc,
              width: "100%",
              height: "200",
              frameborder: "0",
              loading: "lazy",
              referrerpolicy: "no-referrer",
              allowfullscreen: "true",
            },
          })
          .run();
        return true;
      },
      handleKeyDown: (_view, event) => {
        const hasVariableSuggestions =
          textVariableMatchRef.current && textVariableSuggestionsRef.current.length > 0;
        if (event.key === "Escape" && textVariableMatchRef.current) {
          setTextVariableMatch(null);
          return true;
        }
        if (hasVariableSuggestions && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
          event.preventDefault();
          const direction = event.key === "ArrowDown" ? 1 : -1;
          const total = textVariableSuggestionsRef.current.length;
          const nextIndex = (activeVariableIndexRef.current + direction + total) % total;
          setActiveVariableIndex(nextIndex);
          return true;
        }
        if (hasVariableSuggestions && event.key === "Enter") {
          event.preventDefault();
          const variable = textVariableSuggestionsRef.current[activeVariableIndexRef.current];
          if (variable) {
            handleVariableInsert(variable);
            return true;
          }
        }
        return false;
      },
    },
  });

  useEffect(() => {
    if (!autoFocus || !editor) return;
    const raf = requestAnimationFrame(() => {
      editor.commands.focus("end");
      onAutoFocusApplied?.();
    });
    return () => cancelAnimationFrame(raf);
  }, [autoFocus, editor, onAutoFocusApplied]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (listDropdownRef.current && !listDropdownRef.current.contains(e.target as Node)) {
        setListDropdownOpen(false);
      }
      if (
        textVariableDropdownRef.current &&
        !textVariableDropdownRef.current.contains(e.target as Node)
      ) {
        setTextVariableMatch(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!editor) return;

    const updateVariableMatch = () => {
      const prevMatch = textVariableMatchRef.current;
      if (!editor.isFocused) {
        if (prevMatch !== null) setTextVariableMatch(null);
        return;
      }
      const { selection } = editor.state;
      if (!selection.empty) {
        if (prevMatch !== null) setTextVariableMatch(null);
        return;
      }
      const { $from, from } = selection;
      const parentText = $from.parent.textBetween(0, $from.parent.content.size, "\n", "\n");
      const cursor = $from.parentOffset;
      const lastBracket = parentText.lastIndexOf("[", cursor - 1);
      if (lastBracket === -1) {
        if (prevMatch !== null) setTextVariableMatch(null);
        return;
      }
      const query = parentText.slice(lastBracket + 1, cursor);
      if (query.includes("]")) {
        if (prevMatch !== null) setTextVariableMatch(null);
        return;
      }
      const start = $from.start() + lastBracket;
      if (
        prevMatch &&
        prevMatch.from === start &&
        prevMatch.to === from &&
        prevMatch.query === query
      ) {
        return;
      }
      setTextVariableMatch({ from: start, to: from, query });
    };

    editor.on("selectionUpdate", updateVariableMatch);
    editor.on("update", updateVariableMatch);
    editor.on("blur", () => setTextVariableMatch(null));
    return () => {
      editor.off("selectionUpdate", updateVariableMatch);
      editor.off("update", updateVariableMatch);
    };
  }, [editor]);

  // Sync external value changes (e.g. reset)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || "";
    if (current !== incoming && !(current === "<p></p>" && incoming === "")) {
      editor.commands.setContent(incoming, false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const btnClass = (active: boolean) =>
    `p-1 rounded transition-colors ${active ? "bg-gray-500 text-white" : "text-gray-400 hover:text-white hover:bg-gray-600"}`;

  const escapeAttribute = (raw: string) => raw.replace(/"/g, "&quot;");
  const handleInsertEmbed = () => {
    const url = window.prompt("Paste embed URL (https://...)");
    if (!url) return;
    const trimmed = url.trim();
    if (!/^https?:\/\//i.test(trimmed)) {
      window.alert("Please use a full http(s) URL.");
      return;
    }
    const safeUrl = escapeAttribute(trimmed);
    editor.chain().focus().insertContent({
      type: "iframe",
      attrs: {
        src: safeUrl,
        width: "100%",
        height: "200",
        frameborder: "0",
        loading: "lazy",
        referrerpolicy: "no-referrer",
        allowfullscreen: "true",
      },
    }).run();
  };

  const activeListType = editor.isActive("bulletList")
    ? "bullet"
    : editor.isActive("orderedList")
    ? "ordered"
    : editor.isActive("taskList")
    ? "task"
    : null;

  const ActiveListIcon = activeListType === "ordered"
    ? ListOrdered
    : activeListType === "task"
    ? ListChecks
    : List;

  const handleVariableInsert = (variable: TextVariable) => {
    if (!textVariableMatch) return;

    if (variable.isPartial) {
      // Table selected – insert "tablename." after the "[" and keep autocomplete open
      const insertText = variable.insertValue ?? variable.label;
      editor
        .chain()
        .focus()
        .insertContentAt(
          { from: textVariableMatch.from + 1, to: textVariableMatch.to },
          insertText,
        )
        .run();
      // Match will be re-evaluated by the selectionUpdate/update handler
    } else {
      // Field selected – insert full "[table.field]" and close
      editor
        .chain()
        .focus()
        .insertContentAt(
          { from: textVariableMatch.from, to: textVariableMatch.to },
          variable.label,
        )
        .run();
      setTextVariableMatch(null);
    }
  };

  return (
    <div
      className={`hotspot-text-editor relative bg-gray-700 border border-gray-600 rounded-md overflow-visible ${className ?? ""}`}
    >
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-1.5 py-1 border-b border-gray-600">
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBold().run(); }}
          className={btnClass(editor.isActive("bold"))}
          title="Bold"
        >
          <Bold size={12} />
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleItalic().run(); }}
          className={btnClass(editor.isActive("italic"))}
          title="Italic"
        >
          <Italic size={12} />
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleUnderline().run(); }}
          className={btnClass(editor.isActive("underline"))}
          title="Underline"
        >
          <UnderlineIcon size={12} />
        </button>

        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); handleInsertEmbed(); }}
          className={btnClass(false)}
          title="Embed"
        >
          <Code size={12} />
        </button>

        <div className="w-px h-3 bg-gray-600 mx-0.5" />

        {/* List dropdown */}
        <div className="relative" ref={listDropdownRef}>
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); setListDropdownOpen((o) => !o); }}
            className={`flex items-center gap-0.5 px-1 py-1 rounded transition-colors ${activeListType ? "bg-gray-500 text-white" : "text-gray-400 hover:text-white hover:bg-gray-600"}`}
            title="Lists"
          >
            <ActiveListIcon size={12} />
            <ChevronDown size={8} />
          </button>
          {listDropdownOpen && (
            <div className="absolute top-full left-0 mt-0.5 bg-gray-800 border border-gray-600 rounded shadow-lg z-50 min-w-[130px] py-0.5">
              {[
                { label: "Bullet List", icon: List, toggle: () => editor.chain().focus().toggleBulletList().run(), active: editor.isActive("bulletList") },
                { label: "Ordered List", icon: ListOrdered, toggle: () => editor.chain().focus().toggleOrderedList().run(), active: editor.isActive("orderedList") },
                { label: "Task List", icon: ListChecks, toggle: () => editor.chain().focus().toggleTaskList().run(), active: editor.isActive("taskList") },
              ].map(({ label, icon: Icon, toggle, active }) => (
                <button
                  key={label}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); toggle(); setListDropdownOpen(false); }}
                  className={`flex items-center gap-2 w-full px-2.5 py-1.5 text-xs transition-colors ${active ? "bg-gray-600 text-white" : "text-gray-300 hover:bg-gray-700 hover:text-white"}`}
                >
                  <Icon size={12} />
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="w-px h-3 bg-gray-600 mx-0.5" />

        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setTextAlign("left").run(); }}
          className={btnClass(editor.isActive({ textAlign: "left" }))}
          title="Align left"
        >
          <AlignLeft size={12} />
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setTextAlign("center").run(); }}
          className={btnClass(editor.isActive({ textAlign: "center" }))}
          title="Align center"
        >
          <AlignCenter size={12} />
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setTextAlign("right").run(); }}
          className={btnClass(editor.isActive({ textAlign: "right" }))}
          title="Align right"
        >
          <AlignRight size={12} />
        </button>
      </div>

      {/* Editor area */}
      <EditorContent editor={editor} placeholder={placeholder} />

      {textVariableMatch && textVariableSuggestions.length > 0 && (
        <div
          ref={textVariableDropdownRef}
          className="absolute left-2 right-2 top-full z-50 mt-1 rounded-md border border-gray-600 bg-gray-800 shadow-lg"
          onMouseDown={(e) => e.preventDefault()}
        >
          {textVariableSuggestions.map((variable, index) => (
            <button
              key={variable.label}
              type="button"
              className={`flex w-full flex-col items-start gap-0.5 px-2 py-1.5 text-left text-xs ${
                index === activeVariableIndex
                  ? "bg-gray-700 text-white"
                  : "text-gray-200 hover:bg-gray-700"
              }`}
              onClick={() => handleVariableInsert(variable)}
              onMouseEnter={() => setActiveVariableIndex(index)}
              aria-selected={index === activeVariableIndex}
            >
              <span className="font-mono text-gray-100">{variable.label}</span>
              <span className="text-[11px] text-gray-400">{variable.description}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
